import SendIcon from "@mui/icons-material/Send"
import { Stack, Collapse, Alert, AlertTitle, Divider } from "@mui/material"
import dayjs from "dayjs"
import { KeyboardEventHandler, ReactElement } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"

import SubDeviceAssessmentFormHP from "./subDeviceAssessmentFormHP"
import AppLoadingButton from "../../components/buttons/appLoadingButton"
import ValidatedRadioGroup from "../../components/inputs/validated/validatedRadioGroup"
import ValidatedAutocomplete from "../../components/inputs/validated/validatedSelect"
import ValidatedTextField from "../../components/inputs/validated/validatedTextField"
import { LCT_ASSESSOR_STATUS_MAPPINGS } from "../../components/popUps/assessDevice/assessDeviceForm"
import { Datasets } from "../../constants/enums"
import {
    SSC_MIN_3_PHASE,
    IN_ACCORDANCE_WITH_EN_61000_3_11,
    ZMAX,
    COMPLIANCE_STATUS,
    CLASS_A_LIMITS,
    BOOLEAN_RADIO_GROUPS,
    TECHNICAL_REQUIREMENTS,
    APPLY_CONNECT,
    CONNECT_NOTIFY,
    TOTAL_SYSTEM_MAX_DEMAND,
    BOTH_STANDARDS,
    HP_IN_ACCORDANCE_12_STATUSES,
    IN_ACCORDANCE_WITH_EN_61000_3_12,
    mapRegisterNameToHPValues,
    BACK_UP_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12,
    BACK_UP_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11,
    BACK_UP_HEATER_ZMAX,
    BACK_UP_HEATER_SSC_MIN_3_PHASE,
    BOOST_HEATER_ZMAX,
    BOOST_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11,
    BOOST_HEATER_SSC_MIN_3_PHASE,
    BOOST_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12,
    SSC_MIN_SINGLE_PHASE,
    MIN_RSCE_CALCULATED,
    WATER_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11,
    WATER_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12,
    WATER_HEATER_SSC_MIN_3_PHASE,
    WATER_HEATER_ZMAX,
    HEAT_PUMP_IN_ACCORDANCE_WITH_EN_61000_3_11,
    HEAT_PUMP_IN_ACCORDANCE_WITH_EN_61000_3_12,
    HEAT_PUMP_SSC_MIN_3_PHASE,
    HEAT_PUMP_ZMAX,
    FAULT_LEVEL,
    IMPEDANCE_AT_PCC,
    HP_IN_ACCORDANCE_11_STATUSES,
} from "../../constants/lct/evdc/assessDeviceForm"
import { POSITIVE_NUMBER_REGEX } from "../../constants/patterns"
import { SINGLE_DEMAND_DEVICE } from "../../router/routes"
import useAssessDevice from "../../services/middleware/hooks/deviceRegisters/useAssessDevice"
import { IFullHPDevice } from "../../services/middleware/hooks/deviceRegisters/useGetFullDevice"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

export const invalidNumberMessage = "Value must be a positive number"

interface IAssessHPDemandFormProps {
    data: IFullHPDevice
    isLoading: boolean
    mutate: () => void
}

const getSingleDeviceUrl = (deviceRef: string): string =>
    SINGLE_DEMAND_DEVICE.replace(":dataset", "HP").replace(
        ":deviceId",
        encodeURIComponent(deviceRef)
    )

/** Form component for submitting assessment for a demand device.
 */
const AssessHPDemandForm = ({
    data,
    isLoading,
    mutate,
}: IAssessHPDemandFormProps): ReactElement => {
    const hasBackUpHeater = data.header.backUpHeaterStatus === "TRUE"
    const hasBoostHeater = data.header.boostHeaterStatus === "TRUE"
    const hasWaterHeater = data.header.waterHeaterStatus === "TRUE"
    const hasHeatPump = data.header.heatPumpStatus === "TRUE"

    const navigate = useNavigate()

    const { control, handleSubmit, watch } = useForm({
        defaultValues: mapRegisterNameToHPValues(data),
        shouldUnregister: true,
    })

    const { error, isSending, postLctResponse } = useAssessDevice(() => {
        mutate()
        navigate(getSingleDeviceUrl(data.header["Device Ref"]))
    })

    const submitResponse = (): void => {
        const updatedValues = {
            ComplianceStatus: watch(COMPLIANCE_STATUS) as string,
            ClassALimits: watch(CLASS_A_LIMITS) as string,
            TechnicalRequirements: watch(TECHNICAL_REQUIREMENTS) as string,
            BothStandards: watch(BOTH_STANDARDS) as string,
            TotalHeatPumpSystemMaxDemand: watch(
                TOTAL_SYSTEM_MAX_DEMAND
            ) as string,
            ConnectAndNotify: watch(CONNECT_NOTIFY) as string,
            ApplyToConnect: watch(APPLY_CONNECT) as string,
            InAccordanceWithEn61000312: watch(
                IN_ACCORDANCE_WITH_EN_61000_3_12
            ) as string,
            SscMin3Phase: watch(SSC_MIN_3_PHASE) as string,
            SscMinSinglePhase: watch(SSC_MIN_SINGLE_PHASE) as string,
            MinimumRsce: watch(MIN_RSCE_CALCULATED) as string,
            FaultLevel: watch(FAULT_LEVEL) as string,
            ImpedanceAtPcc: watch(IMPEDANCE_AT_PCC) as string,
            InAccordanceWithEn61000311: watch(
                IN_ACCORDANCE_WITH_EN_61000_3_11
            ) as string,
            Zmax: watch(ZMAX) === "" ? null : (watch(ZMAX) as number),
            BackUpHeater_InAccordanceWithEn61000312: watch(
                BACK_UP_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12
            ) as string | null,
            BackUpHeater_SscMin3Phase: watch(BACK_UP_HEATER_SSC_MIN_3_PHASE) as
                | string
                | null,
            BackUpHeater_InAccordanceWithEn61000311: watch(
                BACK_UP_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11
            ) as string | null,
            BackUpHeater_Zmax:
                watch(BACK_UP_HEATER_ZMAX) === ""
                    ? null
                    : (watch(BACK_UP_HEATER_ZMAX) as number | null),
            BoostHeater_InAccordanceWithEn61000312: watch(
                BOOST_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12
            ) as string | null,
            BoostHeater_SscMin3Phase: watch(BOOST_HEATER_SSC_MIN_3_PHASE) as
                | string
                | null,
            BoostHeater_InAccordanceWithEn61000311: watch(
                BOOST_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11
            ) as string | null,
            BoostHeater_Zmax:
                watch(BOOST_HEATER_ZMAX) === ""
                    ? null
                    : (watch(BOOST_HEATER_ZMAX) as number | null),
            WaterHeater_InAccordanceWithEn61000312: watch(
                WATER_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12
            ) as string | null,
            WaterHeater_SscMin3Phase: watch(WATER_HEATER_SSC_MIN_3_PHASE) as
                | string
                | null,
            WaterHeater_InAccordanceWithEn61000311: watch(
                WATER_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11
            ) as string | null,
            WaterHeater_Zmax:
                watch(WATER_HEATER_ZMAX) === ""
                    ? null
                    : (watch(WATER_HEATER_ZMAX) as number | null),
            HeatPump_InAccordanceWithEn61000312: watch(
                HEAT_PUMP_IN_ACCORDANCE_WITH_EN_61000_3_12
            ) as string | null,
            HeatPump_SscMin3Phase: watch(HEAT_PUMP_SSC_MIN_3_PHASE) as
                | string
                | null,
            HeatPump_InAccordanceWithEn61000311: watch(
                HEAT_PUMP_IN_ACCORDANCE_WITH_EN_61000_3_11
            ) as string | null,
            HeatPump_Zmax:
                watch(HEAT_PUMP_ZMAX) === ""
                    ? null
                    : (watch(HEAT_PUMP_ZMAX) as number | null),
        }
        postLctResponse(
            data.header["Device Ref"],
            dayjs(data.header["Published At"]),
            Datasets.HP,
            updatedValues
        )
    }

    const handleKeyPress: KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (event.key === "Enter" && !event.altKey) {
            event.preventDefault()
        }
    }

    return (
        <Stack spacing={GRID_SPACING * 2}>
            <ValidatedRadioGroup
                control={control}
                required
                registerName={CLASS_A_LIMITS}
                label="Class A limits of IEC 61000-3-2?"
                radioOptions={BOOLEAN_RADIO_GROUPS}
            />
            <ValidatedRadioGroup
                control={control}
                required
                registerName={TECHNICAL_REQUIREMENTS}
                label="Technical Requirements of IEC 61000-3-3?"
                radioOptions={BOOLEAN_RADIO_GROUPS}
            />
            <ValidatedRadioGroup
                control={control}
                required
                registerName={BOTH_STANDARDS}
                label="Both Standards for All Parts of System?"
                radioOptions={BOOLEAN_RADIO_GROUPS}
            />
            <ValidatedRadioGroup
                control={control}
                required
                registerName={TOTAL_SYSTEM_MAX_DEMAND}
                label="Total Heat Pump System Maximum Demand ≤32A?"
                radioOptions={BOOLEAN_RADIO_GROUPS}
            />
            <ValidatedRadioGroup
                control={control}
                required
                registerName={CONNECT_NOTIFY}
                label="Connect & Notify?"
                radioOptions={BOOLEAN_RADIO_GROUPS}
            />
            <ValidatedRadioGroup
                control={control}
                required
                registerName={APPLY_CONNECT}
                label="Apply to Connect?"
                radioOptions={BOOLEAN_RADIO_GROUPS}
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={IN_ACCORDANCE_WITH_EN_61000_3_12}
                label="In accordance with EN 61000-3-12"
                options={Object.keys(HP_IN_ACCORDANCE_12_STATUSES)}
                getOptionLabel={(option) =>
                    HP_IN_ACCORDANCE_12_STATUSES[
                        option as keyof typeof HP_IN_ACCORDANCE_12_STATUSES
                    ] ?? ""
                }
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={SSC_MIN_3_PHASE}
                placeholder="SSC Minimum 3 Phase (kVA)"
                label="SSC Minimum 3 Phase (kVA)"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_NUMBER_REGEX,
                        message: invalidNumberMessage,
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={SSC_MIN_SINGLE_PHASE}
                placeholder="SSC Minimum Single Phase (kVA)"
                label="SSC Minimum Single Phase (kVA)"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_NUMBER_REGEX,
                        message: invalidNumberMessage,
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={MIN_RSCE_CALCULATED}
                placeholder="Minimum Rsce calculated"
                label="Minimum Rsce calculated"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_NUMBER_REGEX,
                        message: invalidNumberMessage,
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={FAULT_LEVEL}
                placeholder="Fault level calculated using minimum Rsce (MVA)"
                label="Fault level calculated using minimum Rsce (MVA)"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_NUMBER_REGEX,
                        message: invalidNumberMessage,
                    },
                }}
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={IMPEDANCE_AT_PCC}
                placeholder="Impedance at PCC (Ω)"
                label="Impedance at PCC (Ω)"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_NUMBER_REGEX,
                        message: invalidNumberMessage,
                    },
                }}
            />
            <ValidatedAutocomplete
                control={control}
                required
                registerName={IN_ACCORDANCE_WITH_EN_61000_3_11}
                label="In accordance with EN 61000-3-11"
                options={Object.keys(HP_IN_ACCORDANCE_11_STATUSES)}
                getOptionLabel={(option) =>
                    HP_IN_ACCORDANCE_11_STATUSES[
                        option as keyof typeof HP_IN_ACCORDANCE_11_STATUSES
                    ] ?? ""
                }
            />
            <ValidatedTextField
                control={control}
                required={false}
                registerName={ZMAX}
                placeholder="Zmax (Ω)"
                label="Zmax (Ω)"
                multiline
                handleKeyPress={handleKeyPress}
                customRules={{
                    pattern: {
                        value: POSITIVE_NUMBER_REGEX,
                        message: invalidNumberMessage,
                    },
                }}
            />
            {(hasBackUpHeater ||
                hasBoostHeater ||
                hasWaterHeater ||
                hasHeatPump) && <Divider />}
            {hasBackUpHeater && (
                <SubDeviceAssessmentFormHP
                    control={control}
                    title="Back Up Heater"
                    subtitle="This heat pump has a back up heater. Please complete the following fields related to this additional module"
                    registerNames={[
                        BACK_UP_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12,
                        BACK_UP_HEATER_SSC_MIN_3_PHASE,
                        BACK_UP_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11,
                        BACK_UP_HEATER_ZMAX,
                    ]}
                    handleKeyPress={handleKeyPress}
                />
            )}
            {hasBoostHeater && (
                <SubDeviceAssessmentFormHP
                    control={control}
                    title="Boost Heater"
                    subtitle="This heat pump has a boost heater. Please complete the following fields related to this additional module"
                    registerNames={[
                        BOOST_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12,
                        BOOST_HEATER_SSC_MIN_3_PHASE,
                        BOOST_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11,
                        BOOST_HEATER_ZMAX,
                    ]}
                    handleKeyPress={handleKeyPress}
                />
            )}
            {hasWaterHeater && (
                <SubDeviceAssessmentFormHP
                    control={control}
                    title="Water Heater"
                    subtitle="This heat pump has a water heater. Please complete the following fields related to this additional module"
                    registerNames={[
                        WATER_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_12,
                        WATER_HEATER_SSC_MIN_3_PHASE,
                        WATER_HEATER_IN_ACCORDANCE_WITH_EN_61000_3_11,
                        WATER_HEATER_ZMAX,
                    ]}
                    handleKeyPress={handleKeyPress}
                />
            )}
            {hasHeatPump && (
                <SubDeviceAssessmentFormHP
                    control={control}
                    title="Heat Pump"
                    subtitle="This heat pump has a additional heat pump Please complete the following fields related to this additional module"
                    registerNames={[
                        HEAT_PUMP_IN_ACCORDANCE_WITH_EN_61000_3_12,
                        HEAT_PUMP_SSC_MIN_3_PHASE,
                        HEAT_PUMP_IN_ACCORDANCE_WITH_EN_61000_3_11,
                        HEAT_PUMP_ZMAX,
                    ]}
                    handleKeyPress={handleKeyPress}
                />
            )}
            <ValidatedAutocomplete
                control={control}
                required
                registerName={COMPLIANCE_STATUS}
                label="Set compliance status"
                options={Object.keys(LCT_ASSESSOR_STATUS_MAPPINGS)}
                disablePortal={false}
            />
            <Collapse
                style={{ marginBottom: "16px" }}
                in={!!error}
                mountOnEnter
            >
                <Alert severity={error?.level}>
                    <AlertTitle>{error?.message}</AlertTitle>
                    {error?.displayDetail}
                </Alert>
            </Collapse>

            <AppLoadingButton
                style={{
                    marginLeft: "auto",
                    marginTop: "32px",
                }}
                onClick={handleSubmit(submitResponse)}
                disabled={isLoading || isSending}
                variant="contained"
                loading={isSending}
                endIcon={<SendIcon />}
            >
                Submit Assessment
            </AppLoadingButton>
        </Stack>
    )
}

export default AssessHPDemandForm
